import { Dispatch, SetStateAction, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// assets
import successImage from 'assets/images/success-image.svg'
import loadingCalendar from 'assets/icons/loading-calendar.png'

// local imports
import AuthUtil from 'utils/AuthUtil'
import NotificationDialog from 'components/ui/NotificationDialog'
import { ILactationRoomSearch } from 'types/LactationType'
import { ISideBarModel } from 'types/CalendarType'
import Loader from 'components/ui/Loader'
import FormModal from 'components/ui/modal/FormModal'
import LactationRoomSearchForm from './LactationRoomSearchForm'
import useGetLactationRoomList from 'components/home/on-campus-services/lactation-room/hooks/use-get-lactation-room-list'
import { SearchModalHeader } from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'
import useLactationRoomResModal from 'components/home/on-campus-services/lactation-room/hooks/use-lactation-room-res-modal'
import useGetLactationLocationList from 'components/home/on-campus-services/lactation-room/hooks/use-get-lactation-room-locations'
import LactationResModalWrapper from './LactationResModal'

interface ModalProps {
  modal: boolean
  setModal: Dispatch<SetStateAction<boolean>>
  closeLactationModal: () => void
  selectedDay: ISideBarModel | undefined
  resetAll: Dispatch<SetStateAction<boolean>>
  sidebarAfterSave: () => void
}

export default function LactationRoomSearchModal({
  modal,
  setModal,
  selectedDay,
  resetAll,
  closeLactationModal,
  sidebarAfterSave,
}: ModalProps) {
  const { t } = useTranslation()
  const selectedDayDate = moment(selectedDay?.day.date).toDate()
  const selectedDateStr = moment(selectedDayDate).format('DD.MM.YYYY')

  /* Get Current logged employee infos - (location id) */
  const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId

  /* Filter form default values */
  const defaultValues: ILactationRoomSearch = {
    locationId: employeeLocation ?? 0,
    lactationRoom: null,
    reservationDate: selectedDayDate,
  }
  const filteredDate = defaultValues.reservationDate

  /* Filter form state  */
  const { watch, resetField, control } = useForm<ILactationRoomSearch>({
    defaultValues,
  })

  /* Get doctor search form values */
  const locationId = watch('locationId')
  const _selectedRoom = watch('lactationRoom')

  /* Get Lactation List - Locations | Custom Hooks */
  const { getLactationRoomList, lactationRooms, loading } = useGetLactationRoomList()
  const { data: locationList } = useGetLactationLocationList()

  /* Get Lactation Room List on initial page load  */
  useEffect(() => {
    getLactationRoomList({ locationId: locationId, reservationDate: filteredDate })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId])

  /* Reservation Modal (Open - Close, Selected Lactation Room). | Custom Hook */
  const {
    openModal,
    closeModal,
    modal: isOpen,
    selectedRoom,
    notification,
    setNotification,
  } = useLactationRoomResModal()

  /* Search Modal Methods */
  const handleOnCancel = () => closeLactationModal()
  const selectRoomAndOpen = () => {
    if (!!_selectedRoom) {
      openModal(_selectedRoom)
      setModal(false)
    }
  }

  /* Reservation Scheduler Modal */
  const handleOnCancelReservationModal = () => {
    closeModal()
    setModal(true)
  }

  /* Notification Modal */
  const handleCloseNotification = () => {
    setModal(false)
    setNotification(false)
    resetAll(false)
  }

  return (
    <>
      <FormModal
        id="lactationSelectionModal"
        closable
        header={<span>{t('lactation-res-modal.lactation-reservation')} (1/2)</span>}
        disabled={!_selectedRoom}
        onSave={selectRoomAndOpen}
        visible={modal}
        onHide={handleOnCancel}
        onCancel={handleOnCancel}>
        <SearchModalHeader style={{ marginBottom: '8px' }}>
          <div className="dialog-subheader">
            <span>{t('calendar.selected-day-info-text', { date: selectedDateStr })}</span>
          </div>
        </SearchModalHeader>

        <LactationRoomSearchForm
          control={control}
          resetField={resetField}
          roomList={lactationRooms}
          locationList={locationList}
        />

        {loading ? (
          <div className="my-3 w-100">
            <Loader src={loadingCalendar} />
          </div>
        ) : null}
      </FormModal>

      {/* Lactation Room Res. Scheduler Modal */}
      {!!selectedRoom ? (
        <LactationResModalWrapper
          selectedRoom={selectedRoom}
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          cancelReservationModal={handleOnCancelReservationModal}
          showNotification={() => {
            setNotification(true)
            sidebarAfterSave()
          }}
          filterDate={filteredDate}
          selectedDateStr={selectedDateStr}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('success-text.appointment-completed')}
        content={t('lactation-res-modal.notification-text')}
        firstButtonLabel={t('buttons.back-to-calendar')}
        onHide={handleCloseNotification}
        onSubmit={handleCloseNotification}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}
